import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { NzMessageService } from 'ng-zorro-antd/message';
import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { ActionService, cleanObject, CustomFormatter, CustomValidators, ISitemap, ISitemapCommand, ISitemapModel, SITEMAP } from 'pbc.angular';
import { toInitials } from 'pbc.functions';

import { IFeldKategorie, IPostFeldKategorieResponse } from 'fa-kt.types';
import { FeldKategoriesService } from '../../../query';
import { PostFeldKategorieCommandService } from '../service';

interface IFormValue {
  feldKategorie?: IFeldKategorie;
}

@Component({
  selector: 'fa-kt-post-feld-kategorie',
  templateUrl: './post-feld-kategorie.component.html',
  styleUrls: ['./post-feld-kategorie.component.css'],
})
export class PostFeldKategorieCommandComponent implements OnInit, OnDestroy {
  command!: ISitemapCommand;
  model!: ISitemapModel;

  subscriptions: Subscription[] = [];
  form!: FormGroup;
  $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  CustomFormatter = CustomFormatter;

  @Input() set loading(loading: boolean) {
    this.$loading.next(this.$loading.getValue() || loading);
  }
  @Input() set value(value: IFormValue) {
    this.patch(value);
  }
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() finished: EventEmitter<IPostFeldKategorieResponse> = new EventEmitter<IPostFeldKategorieResponse>();

  constructor(
    @Inject(SITEMAP) private sitemap: ISitemap,
    private fb: FormBuilder,
    private actions: ActionService,
    private message: NzMessageService,
    public feldKategories: FeldKategoriesService,
    public postFeldKategorie: PostFeldKategorieCommandService /* << dependencies    */ /*    dependencies >> */,
  ) {
    this.command = sitemap.FELDER.Commands.POST_FELD_KATEGORIE;
    this.model = sitemap.FELDER.Models.FELD_KATEGORIE;
    /* << constructor    */
    /*    constructor >> */
    this.form = this.fb.group({
      feldKategorie: this.fb.group({
        id: [null, []],
        name: [null, [Validators.required]],
        inApp: [null],
        initialen: [null, []],
        kommentar: [null, []],
        color: [null, []],
        icon: [null, []],
        order: [null, []],
      }),
    });
    this.initialen?.setValidators([CustomValidators.isUnique(this.feldKategories.initialenIsUnique(this.id))]);
    this.patch();
  }

  ngOnInit() {
    this.subscriptions.push(
      this.form.valueChanges.subscribe((value: any) => {
        this.valid.emit(this.form.valid);
        if (this.form.valid) {
          this.actions.subscribe({ key: this.command.translation, action: async () => await this.submit() });
        } else {
          this.actions.unsubscribe(this.command.translation);
        }
      }),
      this.id?.valueChanges.subscribe(async (id) => {
        /* << on-id-change    */
        /*    on-id-change >> */
      }) as Subscription,
      this.name?.valueChanges.subscribe(async (name) => {
        /* << on-name-change    */
        /*    on-name-change >> */
      }) as Subscription,
      this.initialen?.valueChanges.subscribe(async (initialen) => {
        /* << on-initialen-change    */
        /*    on-initialen-change >> */
      }) as Subscription,
      this.kommentar?.valueChanges.subscribe(async (kommentar) => {
        /* << on-kommentar-change    */
        /*    on-kommentar-change >> */
      }) as Subscription,
      /* << subscibe    */
      this.name?.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe((name: string) => {
        if (this.initialen && (!this.initialen.touched || !this.initialen.value)) {
          this.initialen.patchValue(toInitials(name));
        }
      }) as Subscription,
      /*    subscibe >> */
    );
    /* << init    */
    /*    init >> */
  }

  ngOnDestroy() {
    this.actions.unsubscribe(this.command.translation);
    this.subscriptions.filter(($: Subscription) => $ && !$.closed).forEach(($: Subscription) => $.unsubscribe());
  }

  private patch(value?: IFormValue) {
    let feldKategorie = {
      id: null,
      name: null,
      initialen: null,
      kommentar: null,
      order: 0,
      color: '',
      icon: '',
    };
    if (value && value.feldKategorie) {
      feldKategorie = {
        ...feldKategorie,
        ...cleanObject(value.feldKategorie),
      };
    }
    this.form.patchValue({ feldKategorie });
    /* << custom-stuff    */
    /*    custom-stuff >> */
    this.update();
  }

  update() {
    this.$loading.next(true);
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.$valid.next(this.form.valid);
    this.$loading.next(false);
  }

  async submit() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload && payload.feldKategorie) {
        payload.feldKategorie = cleanObject(payload.feldKategorie);
      }
      const response: IPostFeldKategorieResponse | undefined = await this.postFeldKategorie.request(payload);
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls.objektArt.get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }

  get id() {
    return this.form.controls.feldKategorie.get('id');
  }

  get name() {
    return this.form.controls.feldKategorie.get('name');
  }

  get initialen() {
    return this.form.controls.feldKategorie.get('initialen');
  }

  get kommentar() {
    return this.form.controls.feldKategorie.get('kommentar');
  }
}
